import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  IconButton,
  Typography,
  Box,
  Divider,
  CircularProgress,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Snackbar,
  Tooltip,
} from "@mui/material";
import { Delete, Publish, CheckCircle } from "@mui/icons-material";
import { publishChapter } from "../utils/api";
import { t } from "@lingui/macro";

const ChapterList = ({ storyID, chapters = {}, onDelete, onChapterPublished }) => {
  const navigate = useNavigate();
  const [loadingPublishIndex, setLoadingPublishIndex] = useState(null);
  const [successPublishIndex, setSuccessPublishIndex] = useState(null);
  const [loadingDeleteIndex, setLoadingDeleteIndex] = useState(null);
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [chapterToDelete, setChapterToDelete] = useState(null);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState("");
  const [hoveringBlockedPublish, setHoveringBlockedPublish] = useState(false);
  const [hoveringBlockedByPrevious, setHoveringBlockedByPrevious] = useState(null);
  const [loadingPublish, setLoadingPublish] = useState({});
  const [successPublish, setSuccessPublish] = useState({});


  const chaptersArray = Array.isArray(chapters)
    ? chapters.map((chapter, index) => ({ ...chapter, index }))
    : Object.entries(chapters || {}).map(([index, chapter]) => ({ ...chapter, index }));


  const anyDelisted = chaptersArray.some((chap) => chap.delisted === true);


  const handlePublishDisabledReason = (charCount, status, chapterIndex, chaptersArray = []) => {
    //console.log(chaptersArray.map(chap => ({ index: chap.index, delisted: chap.delisted })));

    // console.log(`\nChecking chapter ${chapterIndex}: status=${status}, charCount=${charCount}`);
    // console.log("Chapters Array:", chaptersArray);

    const currentChapter = chaptersArray.find((c) => parseInt(c.index) === parseInt(chapterIndex));

    if (!currentChapter) {
      // console.log(`Chapter ${chapterIndex} not found → Block publishing.`);
      return t`Chapter data is missing.`;
    }

    if (status !== "SAVED" && currentChapter.delisted) {
      // console.log(`Chapter ${chapterIndex} is delisted and not SAVED → Block publishing.`);
      return t`This chapter was delisted, edit it and publish again.`;
    }

    if (charCount < 5000) {
      // console.log(`Chapter ${chapterIndex} is too short → Block publishing.`);
      return t`Chapter is too short (${charCount}/5000).`;
    }
    if (charCount > 60000) {
      // console.log(`Chapter ${chapterIndex} is too long → Block publishing.`);
      return t`Chapter is too long (${charCount}/20000).`;
    }

    // Debug logs for delisted check
    // console.log("Checking for other delisted chapters:");
    // chaptersArray.forEach((c) => {
    //   console.log(
    //     `Chapter ${c.index}: delisted=${c.delisted}, status=${c.status}, isCurrent=${parseInt(c.index) === parseInt(chapterIndex)}`
    //   );
    // }
    //)
    ;

    const hasBlockingDelisted = chaptersArray.some(
      (c) => c.delisted && parseInt(c.index) < parseInt(chapterIndex)
    );


    if (hasBlockingDelisted) {
      // console.log(`Other non-SAVED delisted chapters exist BEFORE ${chapterIndex} → Block publishing.`);
      return t`A previous chapter is delisted. Resolve it first.`;
    }

    // Ensure sequential publishing
    const prevIndex = parseInt(chapterIndex) - 1;
    const prevChapter = chaptersArray.find((c) => parseInt(c.index) === prevIndex);

    if (prevIndex >= 1 && (!prevChapter || !prevChapter.published)) {
      // console.log(`Previous chapter ${prevIndex} is not published → Block publishing.`);
      return t`You must publish Chapter ${prevIndex} first.`;
    }

    // Rule 4: A "SAVED" chapter can only be published if all other chapters (both before and after) are not delisted.
    if (currentChapter.status === "SAVED" && !currentChapter.delisted) {
      const allOthersNotDelisted = chaptersArray.every(
        (c) => parseInt(c.index) !== chapterIndex ? !c.delisted : true
      );
      if (!allOthersNotDelisted) {
        // console.log(`Other chapters (before or after) are delisted → Block publishing.`);
        return t`Another chapter is delisted. Resolve it first.`;
      }
    }

    // console.log(`Chapter ${chapterIndex} is eligible for publishing.`);
    return null;
  };



  const hasBlockingDelistedChapter = chaptersArray.some(
    (c) => c.delisted && parseInt(c.index) < parseInt(c.index)
  );


  const handlePublishChapter = async (chapterIndex) => {
    setLoadingPublish((prev) => ({ ...prev, [chapterIndex]: true }));
    try {
      const response = await publishChapter(storyID, chapterIndex);
      if (response.success) {
        setSuccessPublish((prev) => ({ ...prev, [chapterIndex]: true }));
        setSnackbarMessage(t`Chapter published successfully!`);
        setSnackbarOpen(true);

        // Update the chapter status in UI
        chapters[chapterIndex].status = "PUBLISHED";
        chapters[chapterIndex].published = true;

        if (onChapterPublished) {
          onChapterPublished(chapterIndex);
        }

        // Keep the "Published!" button visible for 2 seconds before disappearing
        setTimeout(() => {
          setSuccessPublish((prev) => ({ ...prev, [chapterIndex]: false }));
        }, 2000);
      }
    } catch (error) {
      console.error(t`Error publishing chapter ${chapterIndex}:`, error);
      setSnackbarMessage(t`Failed to publish chapter ${chapterIndex}: ${error}`);
      setSnackbarOpen(true);
      if (error.includes(t`story must have a cover image`))
        onChapterPublished("MISSING_COVER");
    } finally {
      setLoadingPublish((prev) => ({ ...prev, [chapterIndex]: false }));
    }
  };

  const handleDeleteConfirm = (chapterIndex) => {
    setChapterToDelete(chapterIndex);
    setDeleteConfirmOpen(true);
  };

  // --- Updated delete function:
  const handleDeleteChapter = async () => {
    if (chapterToDelete !== null) {
      // Close the confirmation dialog immediately
      setDeleteConfirmOpen(false);
      // Disable the delete icon button for this chapter and show a spinner
      setLoadingDeleteIndex(chapterToDelete);
      try {
        // Await the deletion (ensure onDelete returns a promise)
        await onDelete(chapterToDelete);
        // Optionally force a delay so the spinner remains visible
        setSnackbarMessage(
          t`Chapter ${chapterToDelete} deleted successfully!`
        );
        setSnackbarOpen(true);
      } catch (error) {
        console.error(`Error deleting chapter ${chapterToDelete}:`, error);
        setSnackbarMessage(
          t`Failed to delete chapter ${chapterToDelete}.`
        );
        setSnackbarOpen(true);
      } finally {
        setLoadingDeleteIndex(null);
      }
    }
  };

  const getStatusColor = (status, isHighlighted) => {
    if (isHighlighted) return "red"; // Highlighted delisted chapters
    switch (status) {
      case "PUBLISHED":
        return "green";
      case "SAVED":
        return "gold";
      case "DELISTED":
        return "red";
      default:
        return "gray";
    }
  };


  const canPublishChapter = (chapterIndex, chaptersArray) => {
    const chapterIdx = parseInt(chapterIndex);

    // Check that if chapterIdx > 1, the previous chapter is published
    if (chapterIdx > 1) {
      const prevChapter = chaptersArray.find((c) => parseInt(c.index) === chapterIdx - 1);
      if (!prevChapter || !prevChapter.published) {
        return false;
      }
    }
    // Find the current chapter
    const currentChapter = chaptersArray.find((c) => parseInt(c.index) === chapterIdx);

    if (!currentChapter) return false; // If chapter doesn't exist, prevent publishing

    // Rule 1: First chapter can always be published if it's "SAVED" if not delisted
    // if (chapterIdx === 1 && currentChapter.status === "SAVED" && !currentChapter.delisted) {
    //   return true;
    // }

    // Rule 2: A chapter that is "DELISTED" (status = "DELISTED") cannot be published
    if (currentChapter.status === "DELISTED") {
      return false;
    }

    // Rule 3: A "DELISTED" but "SAVED" chapter can be published if there are no other delisted chapters before it
    if (currentChapter.delisted && currentChapter.status === "SAVED") {
      const hasEarlierDelisted = chaptersArray.some(
        (c) => c.delisted && parseInt(c.index) < chapterIdx
      );

      //console.log("hasEarlierDelisted", hasEarlierDelisted, chapterIdx);
      return !hasEarlierDelisted;
    }

    // Rule 4: A "SAVED" chapter can only be published if all previous chapters are published
    if (currentChapter.status === "SAVED" && currentChapter.delisted) {
      const allPreviousPublished = chaptersArray.every(
        (c) => parseInt(c.index) < chapterIdx ? c.published : true
      );
      return allPreviousPublished;
    }

    // Rule 4: A "SAVED" annd not delisted chapter can only be published if all other chapters (both before and after) are not delisted.
    if (currentChapter.status === "SAVED" && !currentChapter.delisted) {
      const allOthersNotDelisted = chaptersArray.every(
        (c) => parseInt(c.index) !== chapterIdx ? !c.delisted : true
      );
      return allOthersNotDelisted;
    }

    console.warn("Unhandled case for chapter", chapterIdx, currentChapter);

    return false; // Default to false if no conditions are met
  };

  const statusLabels = {
    PUBLISHED: t`PUBLISHED`,
    SAVED: t`DRAFT`,
    DELISTED: t`DELISTED`,
  };


  return (
    <Box>
      <Typography variant="h5" gutterBottom>
        {t`Chapters`}
      </Typography>

      <List>
        {chaptersArray.map((chapter, idx) => {
          const displayLabel =
            chapter.status === "PUBLISHED" && anyDelisted
              ? t`READY TO PUBLISH`
              : statusLabels[chapter.status] || t`Unknown`;
          return (
            <React.Fragment key={chapter.index}>
              <ListItem
                sx={{
                  transition: "box-shadow 0.3s ease-in-out",
                  boxShadow: hoveringBlockedPublish && chapter.delisted
                    ? "0px 0px 12px 3px rgba(255, 0, 0, 0.8)" // Red glow for delisted chapters
                    : hoveringBlockedByPrevious === chapter.index
                      ? "0px 0px 12px 3px rgba(255, 215, 0, 0.8)" // Gold glow for unpublished previous chapters
                      : "none",
                  "&:hover": {
                    boxShadow: hoveringBlockedPublish && chapter.delisted
                      ? "0px 0px 12px 3px rgba(255, 0, 0, 1)" // Stronger red glow on hover
                      : hoveringBlockedByPrevious === chapter.index
                        ? "0px 0px 12px 3px rgba(255, 215, 0, 1)" // Stronger gold glow on hover
                        : "none",
                  },
                }}
              >

                <ListItemText
                  primary={
                    <Typography
                      variant="body1"
                      component="span"
                      sx={{
                        cursor: "pointer",
                        "&:hover": { textDecoration: "underline" },
                      }}
                      onClick={() =>
                        navigate(`/mystories/${storyID}/${chapter.index}`, {
                          state: {
                            contentUrl: chapter.contentUrl,
                            title: chapter.title,
                            updatedAt: chapter.updatedAt
                              ? (
                                typeof chapter.updatedAt.toDate === 'function'
                                  ? chapter.updatedAt.toDate().toISOString()
                                  : new Date(chapter.updatedAt._seconds * 1000).toISOString()
                              )
                              : null,
                          },
                        })
                      }
                    >
                      {chapter.title || t`Chapter ${chapter.index}`}
                    </Typography>
                  }
                  secondary={
                    <>
                      {(chapter.status === "SAVED" && chapter.delisted) ? (
                        <Tooltip
                          title={t`Your chapter was delisted and you have a new draft. Publish it when you are ready`}
                          placement="top-start"
                          enterTouchDelay={0}
                          slotProps={{
                            tooltip: {
                              sx: { fontSize: "1rem" },
                            },
                          }}
                        >
                          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                            <Box
                              sx={{
                                width: 10,
                                height: 10,
                                borderRadius: "50%",
                                background: "conic-gradient(red 0deg, red 180deg, gold 180deg, gold 360deg)",
                              }}
                            />
                            <Typography variant="caption" color="textSecondary">
                              {displayLabel}
                            </Typography>
                          </Box>
                        </Tooltip>
                      ) : (chapter.status === "SAVED" && chapter.published) ? (
                        <Tooltip title={t`Your chapter is published but you have a new draft`} placement="top-start" enterTouchDelay={0} slotProps={{
                          tooltip: {
                            sx: { fontSize: "1rem" },
                          },
                        }}>
                          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                            <Box
                              sx={{
                                width: 10,
                                height: 10,
                                borderRadius: "50%",
                                background: "conic-gradient(gold 0deg, gold 180deg, green 180deg, green 360deg)",
                              }}
                            />
                            <Typography variant="caption" color="textSecondary">
                              {statusLabels[chapter.status] || t`Unknown`}
                            </Typography>
                          </Box>
                        </Tooltip>
                      ) : (
                        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                          <Box
                            sx={{
                              width: 10,
                              height: 10,
                              borderRadius: "50%",
                              background: getStatusColor(chapter.status),
                            }}
                          />
                          <Typography variant="caption" color="textSecondary">
                            {displayLabel}
                          </Typography>
                        </Box>
                      )}

                      <Typography variant="caption" color="textSecondary">
                        {t`Updated at:`}{" "}
                        {chapter.updatedAt
                          ? new Date(chapter.updatedAt._seconds * 1000).toLocaleString()
                          : t`Unknown`}
                      </Typography>
                    </>
                  }
                />

                <ListItemSecondaryAction
                  sx={{ display: "flex", alignItems: "center" }}
                >
                  {/* Delete Button (Only for last chapter) */}
                  {parseInt(chapter.index) === chaptersArray.length && (
                    <IconButton
                      sx={{ color: "error.main", mr: 1 }}
                      onClick={() => handleDeleteConfirm(chapter.index)}
                      disabled={loadingDeleteIndex === chapter.index}
                    >
                      {loadingDeleteIndex === chapter.index ? (
                        <CircularProgress size={20} color="inherit" />
                      ) : (
                        <Delete />
                      )}
                    </IconButton>
                  )}
                  {/* Publish Button (Only if chapter is not already published) */}
                  {chapter.status !== "PUBLISHED" && (
                    <Tooltip
                      title={
                        handlePublishDisabledReason(
                          chapter.charCount,
                          chapter.status,
                          chapter.index,
                          chaptersArray
                        ) || ""
                      }
                      enterTouchDelay={0}  // Show immediately on touch
                      leaveTouchDelay={2000} // Keep visible longer on mobile
                      onOpen={() => {
                        setHoveringBlockedPublish(true);
                        const prevIndex = parseInt(chapter.index) - 1;
                        const prevChapter = chaptersArray.find((c) => parseInt(c.index) === prevIndex);

                        if (prevChapter && !prevChapter.published) {
                          setHoveringBlockedByPrevious(prevChapter.index); // Highlight the previous unpublished chapter
                        }
                      }}
                      onClose={() => {
                        setHoveringBlockedPublish(false);
                        setHoveringBlockedByPrevious(null);
                      }}
                      disableHoverListener={
                        !handlePublishDisabledReason(
                          chapter.charCount,
                          chapter.status,
                          chapter.index,
                          chaptersArray
                        )
                      }
                    >
                      <span>
                        <Button
                          variant="contained"
                          color={successPublish[chapter.index] ? "success" : "primary"}
                          startIcon={successPublish[chapter.index] ? <CheckCircle /> : <Publish />}
                          onClick={() => handlePublishChapter(chapter.index)}
                          disabled={
                            loadingPublish[chapter.index] ||
                            successPublish[chapter.index] ||
                            chapter.charCount < 5000 ||
                            chapter.charCount > 60000 ||
                            chapter.status === "DELISTED" ||
                            (chapter.status !== "SAVED" && chapter.delisted) || // Prevent publishing only if delisted AND not "SAVED"
                            chaptersArray.some((c) => c.delisted && c.status !== "SAVED" && parseInt(c.index) < parseInt(chapter.index)) ||
                            !canPublishChapter(chapter.index, chaptersArray)
                          }
                          sx={{
                            minWidth: 130,
                            height: 40,
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                          }}
                        >

                          <Box
                            sx={{
                              display: "flex",
                              alignItems: "center",
                              minHeight: 24,
                            }}
                          >
                            <CircularProgress
                              size={20}
                              color="inherit"
                              sx={{
                                mr: 1,
                                visibility:
                                  loadingPublish[chapter.index]
                                    ? "visible"
                                    : "hidden",
                                position: "absolute",
                              }}
                            />
                            <Typography
                              sx={{
                                visibility:
                                  loadingPublish[chapter.index]
                                    ? "hidden"
                                    : "visible",
                              }}
                            >
                              {successPublish[chapter.index]
                                ? t`Published!`
                                : t`Publish`}
                            </Typography>
                          </Box>
                        </Button>
                      </span>
                    </Tooltip>
                  )}
                </ListItemSecondaryAction>
              </ListItem>

              {idx < chaptersArray.length - 1 && <Divider />}
            </React.Fragment>);
        })}
      </List>

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={deleteConfirmOpen}
        onClose={() => setDeleteConfirmOpen(false)}
      >
        <DialogTitle>{t`Confirm Deletion`}</DialogTitle>
        <DialogContent>
          <DialogContentText>
            {t`Are you sure you want to delete Chapter ${chapterToDelete}?`}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setDeleteConfirmOpen(false)}
            color="secondary"
          >
            {t`Cancel`}
          </Button>
          <Button onClick={handleDeleteChapter} color="error" autoFocus>
            {t`Delete`}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar for success messages */}
      <Snackbar
        open={snackbarOpen}
        autoHideDuration={3000}
        onClose={() => setSnackbarOpen(false)}
        message={snackbarMessage}
      />
    </Box>
  );
};

export default ChapterList;
